/**
 * AI CONTEXT
 * Purpose: Quality Check (JARVIS-128) — the artwork of one curriculum's
 *   classes as a review queue: the five most recent classes that still have
 *   undecided images (plus any just finished above them), grouped by day,
 *   with competition entries from those classes in their own section on
 *   top. Writes: rotation (owner ask, Sep 2026), hide (owner ask, Sep 2026 —
 *   catch bad scans right away; STUDENT.HIDE_ARTWORK, restore lives on the
 *   profile's Artwork tab), family share and Don't share for class scans,
 *   one at a time or per section (same `isShared` / `dontShare` flags as
 *   Standout Review; STANDOUT_REVIEW_PAGE UPDATE), and the four-corner
 *   perspective crop (JARVIS-391 — anyone who can open this page can crop
 *   or rotate, same as every other artwork thumbnail). The editor hands
 *   over JPEGs and `saveStudentArtworkCrop` uploads them beside the
 *   untouched original.
 *   Commenting and notifying the class teacher are named follow-ups.
 * Data: useQualityCheckData (class/curriculum one-shot reads; students from
 *   the global students atom — see that file) plus users from useGetAllUsers.
 *   This view applies the semester, weekday and teacher filters plus the
 *   All/Artwork/Competition scope radio, persists them (and the curriculum
 *   and the two Hide finished switches) in userSettings, and hands the
 *   grouped layout to QualityCheckTiles.
 * Referenced by: App route ROUTES.QUALITY_CHECK.
 */
import { ClearOutlined } from "@ant-design/icons"
import {
  Alert,
  App,
  Button,
  Radio,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
} from "antd"
import { useCallback, useMemo, useState } from "react"

import Flex from "#blocks/Flex"
import Firebase from "#classes/Firebase"
import { type ArtworkCropResult } from "#components/ArtworkTiles/ArtworkCropModal"
import { ARTWORK_EDIT_SOURCE } from "#components/ArtworkTiles/ArtworkEditableThumb"
import { buildWallItemsFromStudents } from "#components/ArtworkWall/artworkWallHelpers"
import { type ArtworkWallItem } from "#components/ArtworkWall/artworkWallTypes"
import {
  type ArtworkFieldUpdate,
  buildArtworkDontShareUpdate,
  buildArtworkHiddenUpdate,
  buildArtworkSharedUpdate,
  isClassImageShared,
} from "#components/forms/AddStudent/studentArtworkTilesHelpers"
import { HEADER_HEIGHT } from "#components/Header"
import { USER_SETTINGS } from "#constants/globalConstants"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type StudentArtwork } from "#customTypes/composedTypes/studentTypes"
import {
  type QualityCheckFiltersSetting,
  type QualityCheckScope,
  type Uuid,
} from "#customTypes/globalTypes"
import { invariant } from "#customTypes/types"
import { semesterLookup, SemesterOptions } from "#forms/SemesterDropdown"
import { useGetAllUsers } from "#hooks/newHooks/useGetUser"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import {
  QUALITY_CHECK_STATUS,
  useQualityCheckData,
} from "#hooks/newHooks/useQualityCheckData"
import {
  mergeCropSessionOverride,
  revertCropSessionOverride,
  type SavedCropFields,
} from "#utils/artworkCropHelpers"
import {
  revertStudentArtworkCrop,
  saveStudentArtworkCrop,
  saveStudentArtworkRotation,
  trackArtworkEdit,
} from "#utils/saveStudentArtworkImage"
import { formatSemester, getCurrentSemester } from "#utils/semesterHelpers"

import {
  buildCompetitionEntryIndex,
  buildQualityCheckLayout,
  buildQualityCheckOptions,
  buildStudentLabelIndex,
  countStudents,
  countUnresolvedClassItems,
  DEFAULT_QUALITY_CHECK_FILTERS_SETTING,
  getDontShareAllTargets,
  getShareAllTargets,
  hasActiveFilters,
  indexById,
  matchesCurriculum,
  matchesSemester,
  parseQualityCheckFiltersSetting,
  QUALITY_CHECK_SCOPE,
  type QualityCheckFilters,
  type QualityCheckOption,
  resolvePastClassSessionId,
} from "./qualityCheckHelpers"
import QualityCheckTiles from "./QualityCheckTiles"

const { Title, Text } = Typography

/**
 * antd `Select` shows its placeholder for `null`, so "All" is a real option
 * with this sentinel value and is mapped back to `null` for the predicates.
 */
const ALL_OPTION_VALUE = "__all__"

const EMPTY_SESSIONS: Record<string, { id: string; eventStart: string }[]> = {}

const toSelectValue = <V extends string | number>(value: V | null) =>
  value === null ? ALL_OPTION_VALUE : value

const fromSelectValue = <V extends string | number>(value: V | string) =>
  value === ALL_OPTION_VALUE ? null : (value as V)

const withAll = <V extends string | number>(
  allLabel: string,
  options: QualityCheckOption<V>[]
) => [
  { value: ALL_OPTION_VALUE, label: allLabel },
  ...options.map((option) => ({
    value: option.value,
    label: `${option.label} (${option.count})`,
  })),
]

const formatBytes = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`

/**
 * The crop fields as this session last left them. `undefined` for a field
 * means "cleared", which is what merging over the snapshot's artwork needs
 * to undo a crop without a re-read.
 */
type ArtworkCropOverride = Partial<SavedCropFields> & { rotation?: number }

/** Share / Don't share flags written this session, merged over the snapshot. */
type ArtworkDecisionOverride = Partial<
  Pick<
    StudentArtwork,
    "isShared" | "dontShare" | "dontShareReason" | "classSessionId"
  >
>

/** `artwork.<id>.cropUrl` → `cropUrl`, so one marker rule drives both. */
const toCropOverride = (fieldPaths: readonly string[]): ArtworkCropOverride =>
  Object.fromEntries(
    fieldPaths.map((path) => [path.split(".").pop(), undefined])
  )

const QualityCheckView = () => {
  const theme = useThemeContext()
  const { getUserSetting, setUserSetting, currentUserId } = useUserContext()

  // The whole setting persists in userSettings: the curriculum, the class
  // axes that narrow it, the scope, the semester, and the two Hide finished
  // switches. The persisted value is the initial/fallback so saved filters
  // restore on load; the local override wins once the user interacts
  // (setUserSetting flushes debounced, so state makes the change instant —
  // same pattern as useTableSort).
  const storedFiltersRaw = getUserSetting<QualityCheckFiltersSetting>(
    USER_SETTINGS.QUALITY_CHECK_FILTERS
  )
  const storedFilters = useMemo(
    () => parseQualityCheckFiltersSetting(storedFiltersRaw),
    [storedFiltersRaw]
  )
  const [settingOverride, setSettingOverride] =
    useState<QualityCheckFiltersSetting | null>(null)
  const setting = settingOverride ?? storedFilters
  const { curriculumId, semester, hideFinishedItems, hideFinishedClasses } =
    setting

  const updateSetting = useCallback(
    (patch: Partial<QualityCheckFiltersSetting>) => {
      const next = { ...setting, ...patch }
      setSettingOverride(next)
      setUserSetting(USER_SETTINGS.QUALITY_CHECK_FILTERS, next)
    },
    [setUserSetting, setting]
  )

  const { status, snapshot, error, curricula, refresh } =
    useQualityCheckData(curriculumId)
  const users = useGetAllUsers()

  const filters: QualityCheckFilters = useMemo(
    () => ({
      weekday: setting.weekday,
      teacherId: setting.teacherId,
      scope: setting.scope,
    }),
    [setting.scope, setting.teacherId, setting.weekday]
  )

  // Weekday and teacher narrow ONE curriculum's classes, so a new
  // curriculum starts them over; the scope and semester carry across.
  const onCurriculumChange = useCallback(
    (next: string | undefined) =>
      updateSetting({
        curriculumId: next ?? null,
        weekday: null,
        teacherId: null,
      }),
    [updateSetting]
  )

  // Clear returns the page to its blank slate (owner ask, Sep 2026). The
  // two Hide finished switches are how someone likes to review, not a
  // filter, so they stay.
  const clearFilters = useCallback(
    () =>
      updateSetting({
        ...DEFAULT_QUALITY_CHECK_FILTERS_SETTING,
        semester: getCurrentSemester(),
        hideFinishedItems,
        hideFinishedClasses,
      }),
    [hideFinishedClasses, hideFinishedItems, updateSetting]
  )

  // Pieces hidden from this surface. The snapshot is a one-shot read with
  // no listener echo, so a successful hide write has to remove the tile
  // locally; a later re-read never brings them back (the wall builder skips
  // `isHidden`), so stale ids in the set are harmless.
  const [hiddenIds, setHiddenIds] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  // Share and Don't share written this session flip the tile (and its
  // section's decided state) immediately. A later re-read replaces the
  // artwork and these overrides match the saved flags.
  const [decisionOverrides, setDecisionOverrides] = useState<
    Record<string, ArtworkDecisionOverride>
  >({})
  // Crop fields written this session. Same reason as the two above: the
  // snapshot has no listener echo, so the new (or cleared) `cropUrl` has to
  // be merged in locally or the tile keeps showing the uncropped scan. An
  // explicit `undefined` is how a revert clears a field on merge.
  const [cropOverrides, setCropOverrides] = useState<
    Record<string, ArtworkCropOverride>
  >({})
  const { message } = App.useApp()

  const items = useMemo(() => {
    const built = snapshot ? buildWallItemsFromStudents(snapshot.students) : []
    const visible =
      hiddenIds.size === 0
        ? built
        : built.filter((item) => !hiddenIds.has(item.artwork.id))
    return visible.map((item) => {
      const crop = cropOverrides[item.artwork.id]
      const decision = decisionOverrides[item.artwork.id]
      if (!crop && !decision) return item
      return { ...item, artwork: { ...item.artwork, ...crop, ...decision } }
    })
  }, [cropOverrides, decisionOverrides, hiddenIds, snapshot])

  // Semester scoping happens before everything else (facets included): the
  // read brings a student's whole artwork history, but the surface defaults
  // to the current semester — years of old work under a current-semester
  // banner is exactly the confusion this avoids. "Show All" opts back in.
  const semesterItems = useMemo(
    () => items.filter((item) => matchesSemester(item, semester)),
    [items, semester]
  )

  // The roster students' work from THIS curriculum's classes (and
  // class-less pieces, which belong to no other plan).
  const curriculumItems = useMemo(() => {
    if (!snapshot) return []
    return semesterItems.filter((item) =>
      matchesCurriculum(item, snapshot.classesById, snapshot.curriculumId)
    )
  }, [semesterItems, snapshot])

  const sessionsByClassId = snapshot?.sessionsByClassId ?? EMPTY_SESSIONS
  const reviewItems = useMemo(
    () =>
      curriculumItems.map((item) => {
        const classSessionId = resolvePastClassSessionId(
          item.artwork,
          sessionsByClassId
        )
        if (!classSessionId || item.artwork.classSessionId === classSessionId) {
          return item
        }
        return { ...item, artwork: { ...item.artwork, classSessionId } }
      }),
    [curriculumItems, sessionsByClassId]
  )

  const competitionEntriesByArtworkId = useMemo(
    () => buildCompetitionEntryIndex(snapshot?.students ?? []),
    [snapshot]
  )

  const studentLabelById = useMemo(
    () => buildStudentLabelIndex(snapshot?.students ?? []),
    [snapshot]
  )

  // Rotation writes. The tiles hand back batched absolute targets that may
  // span several students (unlike the profile's Artwork tab, which owns one);
  // group them and write each student's angles as `artwork.<id>.rotation`
  // field paths — same rule as the tab (see `buildArtworkRotationUpdate`): a
  // whole-map write would clobber whatever another writer (an upload, an
  // add-to-portfolio) put there mid-flight.
  const studentsById = useMemo(
    () => indexById(snapshot?.students ?? []),
    [snapshot]
  )

  const studentIdByArtworkId = useMemo(() => {
    const index: Record<string, Uuid> = {}
    for (const item of items) index[item.artwork.id] = item.studentId
    return index
  }, [items])

  // The snapshot's map with this tile's artwork on top, so session writes
  // (a Don't share, a crop) are what the update builders check against.
  const artworkMapFor = useCallback(
    (item: ArtworkWallItem): Record<string, StudentArtwork> => {
      const storedMap = studentsById[item.studentId]?.artwork ?? {}
      const stored = storedMap[item.artwork.id]
      const decision = decisionOverrides[item.artwork.id]
      return {
        ...storedMap,
        [item.artwork.id]: {
          ...(stored ?? item.artwork),
          ...cropOverrides[item.artwork.id],
          ...decision,
          // A guessed session lives on the tile for the share control. The
          // write adds it only through `assignSessionId`, so the map keeps
          // the id that is actually stored.
          classSessionId: stored?.classSessionId ?? decision?.classSessionId,
        },
      }
    },
    [cropOverrides, decisionOverrides, studentsById]
  )

  const assignSessionIdFor = useCallback(
    (item: ArtworkWallItem): string | undefined => {
      const stored = studentsById[item.studentId]?.artwork?.[item.artwork.id]
      if (stored?.classSessionId) return undefined
      return (
        resolvePastClassSessionId(
          stored ?? item.artwork,
          snapshot?.sessionsByClassId ?? EMPTY_SESSIONS
        ) ?? undefined
      )
    },
    [snapshot?.sessionsByClassId, studentsById]
  )

  const handleRotationSave = useCallback(
    async (rotationTargets: Record<string, number>) => {
      const targetsByStudent: Record<Uuid, Record<string, number>> = {}
      for (const [artworkId, degrees] of Object.entries(rotationTargets)) {
        const studentId = studentIdByArtworkId[artworkId]
        if (!studentId) continue
        targetsByStudent[studentId] = {
          ...targetsByStudent[studentId],
          [artworkId]: degrees,
        }
      }
      await Promise.all(
        Object.entries(targetsByStudent).map(
          async ([studentId, studentTargets]) => {
            let saved = false
            try {
              saved = await saveStudentArtworkRotation(
                studentId as Uuid,
                studentsById[studentId]?.artwork,
                studentTargets
              )
            } catch {
              void message.error("Could not save the rotation. Try again.")
              return
            }
            if (!saved) return
            for (const artworkId of Object.keys(studentTargets)) {
              trackArtworkEdit({
                source: ARTWORK_EDIT_SOURCE.QUALITY_CHECK,
                action: "rotate",
                studentId,
                artworkId,
              })
            }
          }
        )
      )
    },
    [message, studentIdByArtworkId, studentsById]
  )

  // Hide writes (STUDENT.HIDE_ARTWORK): catching bad scans during review.
  // Same field-path posture as rotation, but the piece must also leave the
  // LOCAL snapshot — there is no listener echo to do it.
  // Restore is deliberately not offered here; it lives on the profile's
  // Artwork tab behind its "Show hidden" toggle.
  const canHideArtwork = useHasPermission(
    RESOURCES.STUDENT,
    USER_ACTIONS.HIDE_ARTWORK
  )
  const canShareClassImages = useHasPermission(
    RESOURCES.STANDOUT_REVIEW_PAGE,
    USER_ACTIONS.UPDATE
  )

  const handleShareArtwork = useCallback(
    async (item: ArtworkWallItem, isShared: boolean) => {
      invariant(currentUserId != null, "Current user id is not defined!")
      const assignSessionId = assignSessionIdFor(item)
      const update = buildArtworkSharedUpdate(
        artworkMapFor(item),
        item.artwork.id,
        isShared,
        currentUserId,
        isShared ? new Date().toISOString() : undefined,
        assignSessionId
      )
      if (!update) return
      try {
        await Firebase.getInstance().updateDocumentOrThrow(
          DOC_TYPES.STUDENTS,
          item.studentId,
          update
        )
        setDecisionOverrides((previous) => ({
          ...previous,
          [item.artwork.id]: {
            ...previous[item.artwork.id],
            isShared,
            ...(assignSessionId ? { classSessionId: assignSessionId } : {}),
          },
        }))
      } catch {
        void message.error("Could not update image sharing. Try again.")
      }
    },
    [artworkMapFor, assignSessionIdFor, currentUserId, message]
  )

  const handleAllowShare = useCallback(
    async (item: ArtworkWallItem) => {
      const update = buildArtworkDontShareUpdate(
        artworkMapFor(item),
        item.artwork.id,
        false
      )
      if (!update) return
      try {
        await Firebase.getInstance().updateDocumentOrThrow(
          DOC_TYPES.STUDENTS,
          item.studentId,
          update
        )
        setDecisionOverrides((previous) => ({
          ...previous,
          [item.artwork.id]: {
            ...previous[item.artwork.id],
            dontShare: false,
          },
        }))
      } catch {
        void message.error("Could not allow sharing. Try again.")
      }
    },
    [artworkMapFor, message]
  )

  /**
   * One field-path update per student (several pieces of one student merge
   * into one write), then the session override for every piece whose
   * student write landed. Any failed student is reported, not dropped.
   */
  const writeSectionDecisions = useCallback(
    async (
      targets: readonly ArtworkWallItem[],
      buildUpdate: (item: ArtworkWallItem) => ArtworkFieldUpdate | null,
      override: ArtworkDecisionOverride,
      errorText: string
    ): Promise<string[]> => {
      const updatesByStudent: Record<Uuid, ArtworkFieldUpdate> = {}
      const artworkIdsByStudent: Record<Uuid, string[]> = {}
      for (const item of targets) {
        const update = buildUpdate(item)
        if (!update) continue
        updatesByStudent[item.studentId] = {
          ...updatesByStudent[item.studentId],
          ...update,
        }
        artworkIdsByStudent[item.studentId] = [
          ...(artworkIdsByStudent[item.studentId] ?? []),
          item.artwork.id,
        ]
      }
      const studentIds = Object.keys(updatesByStudent)
      const results = await Promise.allSettled(
        studentIds.map((studentId) =>
          Firebase.getInstance().updateDocumentOrThrow(
            DOC_TYPES.STUDENTS,
            studentId,
            updatesByStudent[studentId]
          )
        )
      )
      const saved = studentIds.flatMap((studentId, index) =>
        results[index].status === "fulfilled"
          ? artworkIdsByStudent[studentId]
          : []
      )
      if (saved.length > 0) {
        setDecisionOverrides((previous) => {
          const next = { ...previous }
          for (const artworkId of saved) {
            next[artworkId] = { ...next[artworkId], ...override }
          }
          return next
        })
      }
      if (results.some((result) => result.status === "rejected")) {
        void message.error(errorText)
      }
      return saved
    },
    [message]
  )

  const handleShareAll = useCallback(
    async (targets: ArtworkWallItem[]) => {
      invariant(currentUserId != null, "Current user id is not defined!")
      const sharedAt = new Date().toISOString()
      const assigned: Record<string, string> = {}
      const saved = await writeSectionDecisions(
        getShareAllTargets(targets),
        (item) => {
          const assignSessionId = assignSessionIdFor(item)
          if (assignSessionId) assigned[item.artwork.id] = assignSessionId
          return buildArtworkSharedUpdate(
            artworkMapFor(item),
            item.artwork.id,
            true,
            currentUserId,
            sharedAt,
            assignSessionId
          )
        },
        { isShared: true },
        "Could not share every image. Try again."
      )
      const stamped = saved.filter((artworkId) => assigned[artworkId])
      if (stamped.length === 0) return
      setDecisionOverrides((previous) => {
        const next = { ...previous }
        for (const artworkId of stamped) {
          next[artworkId] = {
            ...next[artworkId],
            classSessionId: assigned[artworkId],
          }
        }
        return next
      })
    },
    [artworkMapFor, assignSessionIdFor, currentUserId, writeSectionDecisions]
  )

  const handleDontShareAll = useCallback(
    async (targets: ArtworkWallItem[], reason: string) => {
      await writeSectionDecisions(
        getDontShareAllTargets(targets),
        (item) =>
          isClassImageShared(item.artwork)
            ? null
            : buildArtworkDontShareUpdate(
                artworkMapFor(item),
                item.artwork.id,
                true,
                reason
              ),
        { dontShare: true, isShared: false, dontShareReason: reason },
        "Could not mark every image Don't share. Try again."
      )
    },
    [artworkMapFor, writeSectionDecisions]
  )

  const handleHideArtwork = useCallback(
    async (item: ArtworkWallItem) => {
      invariant(currentUserId != null, "Current user id is not defined!")
      const update = buildArtworkHiddenUpdate(
        studentsById[item.studentId]?.artwork,
        item.artwork.id,
        true,
        currentUserId
      )
      if (!update) return
      await Firebase.getInstance().updateDocument(
        DOC_TYPES.STUDENTS,
        item.studentId,
        update
      )
      setHiddenIds((previous) => new Set(previous).add(item.artwork.id))
    },
    [currentUserId, studentsById]
  )

  // Perspective crop writes (JARVIS-391). Anyone who can see this page can
  // crop; hide stays on its own permission. One angle per piece applies at
  // display time to the original and the crop alike.
  const handleCropSave = useCallback(
    async (item: ArtworkWallItem, result: ArtworkCropResult) => {
      invariant(currentUserId != null, "Current user id is not defined!")
      // The snapshot is one-shot, so a crop written earlier this session
      // lives on the tile, not in `studentsById`. The save must see that
      // entry or it will refuse to write a skeleton.
      const crop = await saveStudentArtworkCrop({
        studentId: item.studentId,
        artworkMap: artworkMapFor(item),
        artwork: item.artwork,
        result,
        userId: currentUserId,
      })
      setCropOverrides((previous) => ({
        ...previous,
        [item.artwork.id]: mergeCropSessionOverride(
          previous[item.artwork.id],
          crop,
          result.rotation
        ),
      }))
      trackArtworkEdit({
        source: ARTWORK_EDIT_SOURCE.QUALITY_CHECK,
        action: crop ? "crop" : "rotate",
        studentId: item.studentId,
        artworkId: item.artwork.id,
      })
    },
    [artworkMapFor, currentUserId]
  )

  const handleCropRevert = useCallback(async (item: ArtworkWallItem) => {
    // The snapshot is a one-shot read, so a crop written this session
    // lives on the tile (`item.artwork`), not in `studentsById`. Revert
    // has to clear that visible crop.
    const fieldPaths = await revertStudentArtworkCrop(
      item.studentId,
      item.artwork
    )
    if (!fieldPaths) return
    setCropOverrides((previous) => ({
      ...previous,
      [item.artwork.id]: revertCropSessionOverride(
        previous[item.artwork.id],
        toCropOverride(fieldPaths)
      ),
    }))
    trackArtworkEdit({
      source: ARTWORK_EDIT_SOURCE.QUALITY_CHECK,
      action: "revert",
      studentId: item.studentId,
      artworkId: item.artwork.id,
    })
  }, [])

  const joins = useMemo(
    () => ({
      classesById: snapshot?.classesById ?? {},
      usersById: indexById(users),
      competitionEntriesByArtworkId,
    }),
    [competitionEntriesByArtworkId, snapshot, users]
  )

  const options = useMemo(
    () => buildQualityCheckOptions(curriculumItems, joins, filters),
    [curriculumItems, filters, joins]
  )

  const layout = useMemo(
    () =>
      buildQualityCheckLayout({
        items: reviewItems,
        classesById: joins.classesById,
        entriesByArtworkId: competitionEntriesByArtworkId,
        studentLabelById,
        filters,
        hideFinishedItems,
        hideFinishedClasses,
      }),
    [
      competitionEntriesByArtworkId,
      filters,
      reviewItems,
      hideFinishedClasses,
      hideFinishedItems,
      joins.classesById,
      studentLabelById,
    ]
  )

  const studentCount = useMemo(
    () => countStudents(layout.renderedItems),
    [layout.renderedItems]
  )

  const unresolvedCount = useMemo(
    () => countUnresolvedClassItems(curriculumItems, joins.classesById),
    [curriculumItems, joins.classesById]
  )

  // A stored curriculum from another semester is not in this semester's
  // catalog; keep it listed (by name when the snapshot has it) so the
  // Select shows a label and the choice can be undone.
  const curriculumOptions = useMemo(() => {
    const listed = curricula.map((curriculum) => ({
      value: curriculum.id,
      label: curriculum.name,
    }))
    if (!curriculumId || curricula.some((c) => c.id === curriculumId)) {
      return listed
    }
    return [
      ...listed,
      {
        value: curriculumId,
        label:
          snapshot?.curriculaById[curriculumId]?.name ?? "Unknown curriculum",
      },
    ]
  }, [curricula, curriculumId, snapshot])

  const isLoading = status === QUALITY_CHECK_STATUS.LOADING
  // The curriculum and a non-default semester are clearable state too.
  const filtersActive =
    hasActiveFilters(filters) ||
    curriculumId !== null ||
    semester !== getCurrentSemester()
  const emptyText = (() => {
    if (isLoading && !snapshot) return "Loading artwork…"
    if (snapshot && snapshot.students.length === 0) {
      return `No students in this curriculum are enrolled in ${formatSemester(snapshot.semester)}.`
    }
    if (items.length === 0) return "No artwork yet for this curriculum."
    if (semesterItems.length === 0) {
      return `No artwork from ${semesterLookup(semester)} in this curriculum — try "Show All" semesters.`
    }
    if (curriculumItems.length === 0) {
      return "No artwork from this curriculum's classes yet."
    }
    if (filters.scope === QUALITY_CHECK_SCOPE.COMPETITION) {
      return "No competition entries in these classes match these filters."
    }
    if (hideFinishedClasses) {
      return "Every class here is decided, or nothing matches these filters."
    }
    return "No artwork matches these filters."
  })()

  return (
    <div
      data-testid="quality-check-view"
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100vw",
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        background: `linear-gradient(to right, ${theme.body1}, ${theme.body2})`,
      }}
    >
      <Flex
        col
        gap={10}
        style={{ padding: "12px 20px 10px", flex: "0 0 auto" }}
      >
        <Flex gap={16} wrap style={{ alignItems: "center" }}>
          <Title level={4} style={{ margin: 0 }}>
            Quality Check
          </Title>
          <Select<string>
            data-testid="qc-curriculum"
            aria-label="Curriculum"
            placeholder="Select a curriculum"
            value={curriculumId ?? undefined}
            onChange={onCurriculumChange}
            options={curriculumOptions}
            showSearch
            allowClear
            optionFilterProp="label"
            style={{ minWidth: 260, maxWidth: 420 }}
          />
          {/* No Refresh button (owner decision, Sep 2026): the one-shot read
              re-runs on any curriculum change, and staleness is rare enough
              that re-picking (or the error alert's Retry) covers it. The
              loader keeps `refresh` for that Retry. */}
          {snapshot ? (
            <Text type="secondary" data-testid="qc-load-stats">
              {formatSemester(snapshot.semester)} · roster{" "}
              {snapshot.stats.rosterSize} → {snapshot.stats.studentCount}{" "}
              students · {snapshot.stats.rosterClassCount} classes ·{" "}
              {formatBytes(snapshot.stats.approxBytes)} in{" "}
              {snapshot.stats.durationMs} ms
            </Text>
          ) : null}
        </Flex>

        <Flex gap={12} wrap style={{ alignItems: "center" }}>
          {/* Same options component as the table filter rows. Scopes the
              artwork (a piece's own semester), NOT the roster read. */}
          <Select
            data-testid="qc-filter-semester"
            aria-label="Semester"
            value={semester}
            onChange={(next: string) => updateSetting({ semester: next })}
            disabled={!snapshot}
            style={{ width: 170 }}
          >
            {SemesterOptions({ includeAll: true })}
          </Select>
          <Select
            data-testid="qc-filter-weekday"
            aria-label="Day of week"
            value={toSelectValue(filters.weekday)}
            onChange={(value) =>
              updateSetting({ weekday: fromSelectValue<number>(value) })
            }
            options={withAll("All days", options.weekdays)}
            disabled={!snapshot}
            style={{ width: 180 }}
          />
          <Select
            data-testid="qc-filter-teacher"
            aria-label="Teacher"
            value={toSelectValue(filters.teacherId)}
            onChange={(value) =>
              updateSetting({ teacherId: fromSelectValue<string>(value) })
            }
            options={withAll("All teachers", options.teachers)}
            disabled={!snapshot}
            showSearch
            optionFilterProp="label"
            style={{ width: 240 }}
          />
          {/* Raw antd Radio.Group, NOT the RadioGroup building block: the
              block inserts a 10px gap that breaks antd's joined segmented
              look, and the three scopes should read as one control. Wrapped
              in a span because the group type accepts no data-* attributes. */}
          <span data-testid="qc-filter-scope">
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={filters.scope}
              onChange={(event) =>
                updateSetting({
                  scope: event.target.value as QualityCheckScope,
                })
              }
              disabled={!snapshot}
              options={[
                {
                  value: QUALITY_CHECK_SCOPE.ALL,
                  label: `All (${options.scopeCounts[QUALITY_CHECK_SCOPE.ALL]})`,
                },
                {
                  value: QUALITY_CHECK_SCOPE.ARTWORK,
                  label: `Artwork (${options.scopeCounts[QUALITY_CHECK_SCOPE.ARTWORK]})`,
                },
                {
                  value: QUALITY_CHECK_SCOPE.COMPETITION,
                  label: `Competition (${options.scopeCounts[QUALITY_CHECK_SCOPE.COMPETITION]})`,
                },
              ]}
            />
          </span>
          <Space size={6}>
            <Switch
              data-testid="qc-hide-finished-items"
              aria-label="Hide finished items"
              checked={hideFinishedItems}
              onChange={(checked) =>
                updateSetting({ hideFinishedItems: checked })
              }
            />
            <Text>Hide finished items</Text>
          </Space>
          <Space size={6}>
            <Switch
              data-testid="qc-hide-finished-classes"
              aria-label="Hide finished classes"
              checked={hideFinishedClasses}
              onChange={(checked) =>
                updateSetting({ hideFinishedClasses: checked })
              }
            />
            <Text>Hide finished classes</Text>
          </Space>
          <Button
            icon={<ClearOutlined />}
            onClick={clearFilters}
            disabled={!filtersActive}
            data-testid="qc-clear-filters"
          >
            Clear
          </Button>
        </Flex>

        <Flex col gap={0}>
          <Text strong data-testid="qc-counts">
            <span data-testid="qc-count-artwork">
              {layout.renderedItems.length}
            </span>{" "}
            {layout.renderedItems.length === 1 ? "piece" : "pieces"} ·{" "}
            <span data-testid="qc-count-students">{studentCount}</span>{" "}
            {studentCount === 1 ? "student" : "students"}
          </Text>
          {unresolvedCount > 0 ? (
            <Text
              type="secondary"
              data-testid="qc-unresolved"
              style={{ fontSize: 12 }}
            >
              {unresolvedCount === 1
                ? "1 piece has no class record and only shows"
                : `${unresolvedCount} pieces have no class record and only show`}{" "}
              with day and teacher on All.
            </Text>
          ) : null}
        </Flex>

        {filters.scope === QUALITY_CHECK_SCOPE.COMPETITION ? (
          <Text type="secondary" data-testid="qc-competition-note">
            Entries from the classes on screen, labeled with the student and
            their submission box. Only this curriculum&apos;s students are
            loaded — pick another curriculum to judge the rest of the field.
          </Text>
        ) : null}

        {error !== null ? (
          <Alert
            type="error"
            showIcon
            message="Could not load this curriculum"
            description={error}
            action={
              <Button size="small" onClick={refresh}>
                Retry
              </Button>
            }
            data-testid="qc-error"
          />
        ) : null}
      </Flex>

      {!curriculumId ? (
        <Flex
          col
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            padding: "32px 24px",
            minHeight: 0,
          }}
          data-testid="qc-pick-curriculum"
        >
          <Title
            level={3}
            type="secondary"
            style={{ margin: 0, textAlign: "center", maxWidth: 520 }}
          >
            Select a curriculum above to review student artwork.
          </Title>
        </Flex>
      ) : isLoading && !snapshot ? (
        <Flex
          col
          gap={16}
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            minHeight: 0,
          }}
          data-testid="qc-loading"
        >
          <Spin size="large" />
          <Title level={4} type="secondary" style={{ margin: 0 }}>
            Loading artwork…
          </Title>
        </Flex>
      ) : (
        <QualityCheckTiles
          layout={layout}
          emptyText={emptyText}
          onRotate={handleRotationSave}
          onShare={canShareClassImages ? handleShareArtwork : undefined}
          onAllow={canShareClassImages ? handleAllowShare : undefined}
          onShareAll={canShareClassImages ? handleShareAll : undefined}
          onDontShareAll={canShareClassImages ? handleDontShareAll : undefined}
          onHide={canHideArtwork ? handleHideArtwork : undefined}
          onCropSave={handleCropSave}
          onCropRevert={handleCropRevert}
        />
      )}
    </div>
  )
}

export default QualityCheckView
